import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import { supabase } from '../services/supabase';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetInfo, setResetInfo] = useState('');
  const [resetting, setResetting] = useState(false);

  // Recuperación de contraseña. Reusa el flujo nativo de Supabase Auth:
  // `resetPasswordForEmail` envía el email de "recovery" cuyo enlace aterriza
  // en `/reset-password` con una sesión de recuperación. Anti-enumeración: el
  // mensaje es SIEMPRE neutro (no revela si el email existe) y los errores del
  // servidor se silencian.
  async function handleForgotPassword() {
    setError('');
    setResetInfo('');
    if (!email) {
      setError('Escribe tu email arriba y vuelve a pulsar "¿Olvidaste tu contraseña?".');
      return;
    }
    setResetting(true);
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch {
      // Silenciado a propósito: no revelamos si la cuenta existe.
    } finally {
      setResetting(false);
      setResetInfo(
        'Si ese email pertenece a una cuenta del club, te hemos enviado un correo para restablecer tu contraseña.'
      );
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;
      if (!data.user) throw new Error('Usuario no encontrado');

      const { data: profile, error: errorPerfil } = await supabase
        .from('profiles')
        .select('role, active')
        .eq('id', data.user.id)
        .single();

      // ⚠️ EL GATE NO SE ABRE. Lo que cambia es lo que se dice.
      //
      // Con el `error` descartado, una lectura caída dejaba `profile` en null
      // y caía en el gate de abajo: cierre de sesión y «Acceso restringido.
      // Solo personal activo del club». O sea que a un admin con su
      // contraseña correcta se le decía QUE NO ES PERSONAL DEL CLUB — y es
      // palabra por palabra el mensaje de una cuenta revocada de verdad, así
      // que no hay forma de distinguirlo: en vez de volver a intentarlo,
      // llamas al club a preguntar por qué te han dado de baja.
      //
      // Fallar cerrado es lo correcto aquí (no se puede dejar una sesión
      // abierta con el rol sin comprobar), así que el `signOut` se queda. Lo
      // que se separa es el motivo.
      if (errorPerfil) {
        await supabase.auth.signOut();
        throw new Error(
          'Tu contraseña es correcta, pero no se ha podido comprobar tu perfil. No es que no tengas acceso: vuelve a intentarlo en un momento.'
        );
      }

      // Gate de rol + cuenta activa. `active` es CRÍTICO: el revoke de un
      // director (mig 145 admin_deactivate_director) pone active=false; sin
      // este chequeo un director desactivado seguiría entrando por web.
      if (
        !profile ||
        profile.active !== true ||
        !['admin', 'director', 'monitor'].includes(profile.role)
      ) {
        await supabase.auth.signOut();
        throw new Error('Acceso restringido. Solo personal activo del club.');
      }

      navigate(profile.role === 'monitor' ? '/monitor/dashboard' : '/dashboard', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al iniciar sesión';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 rounded-full bg-gold-primary/5 blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 rounded-full bg-gold-primary/3 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/logo.png" alt="VERTEX Performance" className="h-32 mx-auto" />
        </div>

        {/* Card */}
        <div className="card shadow-gold">
          <h2 className="font-oswald text-xl uppercase tracking-wider text-white mb-6">
            Iniciar Sesión
          </h2>

          {error && (
            <div className="flex items-center gap-2 bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
              <AlertCircle size={16} className="text-error flex-shrink-0" />
              <p className="text-error text-sm font-inter">{error}</p>
            </div>
          )}

          {resetInfo && (
            <div
              className="flex items-center gap-2 bg-success/10 border border-success/30 rounded-lg p-3 mb-4"
              data-testid="login-reset-info"
            >
              <Mail size={16} className="text-success flex-shrink-0" />
              <p className="text-success text-sm font-inter">{resetInfo}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                <input
                  type="email"
                  className="input pl-9"
                  placeholder="admin@vertexperformance.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="label">Contraseña</label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input pl-9 pr-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full mt-2 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-bg-primary/30 border-t-bg-primary rounded-full animate-spin" />
                  Accediendo...
                </>
              ) : (
                'Acceder al Panel'
              )}
            </button>

            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetting}
              className="w-full text-center text-sm text-gold-primary hover:opacity-80 transition-opacity font-inter disabled:opacity-50"
              data-testid="login-forgot-password"
            >
              {resetting ? 'Enviando…' : '¿Olvidaste tu contraseña?'}
            </button>
          </form>
        </div>

        <p className="text-text-secondary text-base text-center mt-6 font-inter tracking-widest">
          Break your limits!!!
        </p>
        <p className="text-text-secondary text-xs text-center mt-2 font-inter opacity-60 tracking-wider">
          by Cognix Labs
        </p>
      </div>
    </div>
  );
}
